import { vehicleTypes } from "@/utils/functions";
import BuyVehicleView from "./view";
import { useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import useVehicles from "@/hooks/register/useVehicles";
import { PropsSet, PropsView } from "./Models";
import useCybersourceService from "@/hooks/useCybersourceService";
import useVehicleForm from "@/hooks/useVehicleForm";
import { BulkCreateResult } from "@/pages/register/Models/vehicle";
import { bulkCreateVehicles } from "@/redux/ducks/customer";
import { useDispatch } from "react-redux";
import * as xlsx from "xlsx";
import { useSingleFlag } from "@/hooks/useFlags";

const MAX_BULK_PLATES_PER_FILE = 20;

export default function BuyVehicle() {

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isEnabled: isPJEnabled } = useSingleFlag('PD_PJ');

  const { addCyberSourceScripts, getSessionId } = useCybersourceService();

  const [vehicleInfos, setVehicleInfos] = useState<{
    brandName?: string;
    modelName?: string;
  } | null>(null);

  const resetFields: (fields: string[], set: PropsSet['set']) => void = (fields, set) => {
    fields?.map(item => set(item as "type" | "brand" | "model", "0", { shouldValidate: true }));
  };

  const { requestBrands, requestModels, data, isLoadingOrdering } = useVehicles();

  const {
    toggleModal,
    isVisibleModal,
    isLoading,
    isDisabled,
    register,
    handleSubmit,
    errors,
    handlePlate,
    error,
    errorPlate,
    control,
    setValue,
    getValues,
    success,
    errorCreateVehicle,
  } = useVehicleForm({
    navigate,
    resetFields,
    getVisitorId: getSessionId,
    isLogged: true,
    vehicleInfos: vehicleInfos!,
  });

  const [isBulkModalVisible, setIsBulkModalVisible] = useState(false);

  const [bulkState, setBulkState] = useState<{
    selectedFile: File | null;
    isBulkLoading: boolean;
    bulkProgress: number;
    bulkResult: BulkCreateResult | null;
  }>({
    selectedFile: null,
    isBulkLoading: false,
    bulkProgress: 0,
    bulkResult: null,
  });

  const progressTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const toggleBulkModal = () => {
    setIsBulkModalVisible(prev => !prev);
    if (isBulkModalVisible) onBulkReset();
  };

  const onFileChange = (file: File | null) => {
    if (!file) {
      setBulkState(prev => ({ ...prev, selectedFile: null, bulkResult: null }));
      return;
    }
    const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    if (ext !== '.xls' && ext !== '.xlsx') {
      setBulkState(prev => ({
        ...prev,
        selectedFile: null,
        bulkResult: { code: -1, message: 'Formato inválido. Use apenas arquivos .xls ou .xlsx', errors: [] },
      }));
      return;
    }
    setBulkState(prev => ({ ...prev, selectedFile: file, bulkResult: null }));
  };

  const onBulkReset = () => {
    if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    setBulkState({ selectedFile: null, isBulkLoading: false, bulkProgress: 0, bulkResult: null });
  };

  const onBulkSubmit = () => {
    if (!bulkState.selectedFile) return;

    const ext = bulkState.selectedFile.name.toLowerCase().slice(bulkState.selectedFile.name.lastIndexOf('.'));
    if (ext !== '.xls' && ext !== '.xlsx') {
      setBulkState(prev => ({
        ...prev,
        bulkResult: { code: -1, message: 'Formato inválido. Use apenas arquivos .xls ou .xlsx', errors: [] },
      }));
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = xlsx.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows: unknown[][] = xlsx.utils.sheet_to_json(sheet, { header: 1 });

        const items = rows
          .slice(1)
          .map((row, idx) => ({
            line: idx + 2,
            plate: String((row as unknown[])[0] ?? '').trim().toUpperCase(),
          }))
          .filter(item => item.plate.length > 0);

        if (items.length > MAX_BULK_PLATES_PER_FILE) {
          setBulkState(prev => ({
            ...prev,
            bulkResult: {
              code: -1,
              message: `O arquivo contém ${items.length} placas. O limite é ${MAX_BULK_PLATES_PER_FILE} placas por arquivo. Divida em arquivos menores ou remova linhas extras e tente novamente.`,
              errors: [],
            },
          }));
          return;
        }

        setBulkState(prev => ({ ...prev, isBulkLoading: true, bulkProgress: 0 }));

        progressTimerRef.current = setInterval(() => {
          setBulkState(prev => {
            if (prev.bulkProgress >= 90) {
              if (progressTimerRef.current) clearInterval(progressTimerRef.current);
              return prev;
            }
            return { ...prev, bulkProgress: prev.bulkProgress + 5 };
          });
        }, 300);

        dispatch(
          bulkCreateVehicles({
            items,
            callback: (success, result) => {
              if (progressTimerRef.current) clearInterval(progressTimerRef.current);
              setBulkState(prev => ({
                ...prev,
                isBulkLoading: false,
                bulkProgress: 100,
                bulkResult: success && result
                  ? result
                  : { code: -1, message: 'Houve um problema ao processar o arquivo. Tente novamente.', errors: [] },
              }));
            },
          })
        );
      } catch {
        setBulkState(prev => ({
          ...prev,
          bulkResult: { code: -1, message: 'Erro ao ler o arquivo. Verifique o formato e tente novamente.', errors: [] },
        }));
      }
    };
    reader.readAsArrayBuffer(bulkState.selectedFile);
  };

  const goBack = () => navigate(-1);

  const onChangeSelect: PropsView['onChangeSelect'] = ({ e, onChange }) => {
    onChange(e);

    const { name, value, options, selectedIndex } = e.target;

    const text = options?.[selectedIndex]?.text;

    let fields: Array<"type" | "brand" | "model"> = [];

    switch (name) {
      case "type":
        fields = ["brand", "model"];
        if (value !== "0") {
          requestBrands(value);
        }
        break;

      case "brand":
        fields = ["model"];
        if (value !== "0") {
          setVehicleInfos(prev => ({ ...prev, brandName: text }));
          requestModels(getValues("type"), value);
        }
        break;

      case "model":
        if (value !== "0") {
          setVehicleInfos(prev => ({ ...prev, modelName: text }));
        }
        break;

      default:
        break;
    }

    resetFields(fields, setValue as PropsSet['set']);
  }

  useEffect(() => {
    addCyberSourceScripts();
  }, []);

  return <BuyVehicleView
    goBack={goBack}
    isPJEnabled={isPJEnabled}
    onChangeSelect={onChangeSelect}
    isDisabled={isDisabled}
    types={vehicleTypes}
    brands={data?.brands as PropsView['brands']}
    models={data?.models as PropsView['models']}
    isLoading={isLoading || isLoadingOrdering}
    error={error}
    errorPlate={errorPlate}
    handlePlate={handlePlate}
    errorCreateVehicle={errorCreateVehicle}
    success={success}
    register={register}
    control={control}
    isVisibleModal={isVisibleModal}
    toggleModal={toggleModal}
    errors={errors}
    handleSubmit={handleSubmit}
    isVisibleBulkModal={isBulkModalVisible}
    toggleBulkModal={toggleBulkModal}
    selectedFile={bulkState.selectedFile}
    onFileChange={onFileChange}
    isBulkLoading={bulkState.isBulkLoading}
    bulkProgress={bulkState.bulkProgress}
    bulkResult={bulkState.bulkResult}
    onBulkSubmit={onBulkSubmit}
    onBulkReset={onBulkReset}
  />;
}
